import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { httpClient } from '../api/http';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Alert } from '@piggytechng/component-library';
import { useDispatch } from 'react-redux';
import { setModalActions, setUSDHeldRequest, setUSDRequest, setUSDRequestMetrics } from '../store/slices/utils';
import { queryParamBuilder } from '../helpers/utils';
// import useRolePermissions from './useRolePermissions';
import dayjs from 'dayjs';
import { TransactionsParamsType, UpdateUSDDocumentFilePayload } from '../types';

const useUSDRequest = (requestType: 'all' | 'held' = 'all') => {
  // const { restrictActionByRole } = useRolePermissions({});

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState<boolean>(false);
  const [viewTodaysRequest, setViewTodaysRequest] = useState<boolean>(false);
  const [isUpdatingDocument, setIsUpdatingDocument] = useState<boolean>(false);
  const [isUpdatingDocumentFile, setIsUpdatingDocumentFile] = useState<boolean>(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [USDTimeParams, setUSDTimeParams] = useState<TransactionsParamsType>({
    limit: 500,
    search: '',
    status: '',
    start_date: {
      name: 'All Time',
      date: '',
    },
    end_date: {
      name: 'All Time',
      date: '',
    },
    isTimeFilterApplied: false,
  });

  const [usdRequestsParams, setUsdRequestsParams] = useState({
    limit: 50,
    start: '',
    end: '',
    search: '',
    userId: '',
    status: '',
    startDate: '',
    endDate: '',
    cursor: '',
  });

  const [returnForm, setReturnForm] = useState({
    reason: '',
    comment: '',
    returnedPage: [] as string[],
  });

  const showTodaysRequest = () => {
    const today = dayjs(new Date()).format('YYYY-MM-DD');
    const tomorrow = dayjs(new Date()).add(1, 'day').format('YYYY-MM-DD');

    setViewTodaysRequest(true);

    fetchUSDRequests({
      limit: 2000,
      start: today,
      end: tomorrow,
      cursor: '',
      search: '',
      status: '',
      page: 1,
    });
  };

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const params = useParams();
  const requestId = params.id as string;
  const userId = params.userId as string;

  const [isTest] = useState(() => queryParams.get('test') === 'true');

  const [usdRequestsData, setUsdRequestsData] = useState({
    documents: [],
    cursor: '',
    currentPage: 1,
    nextPage: 1,
    lastPage: 0,
    total: 0,
  });

  const canResetSearch = useMemo(() => {
    return usdRequestsParams.search !== '' || usdRequestsParams.userId !== '';
  }, [usdRequestsParams]);

  const canResetFilter = useMemo(() => {
    return (
      (usdRequestsParams.start !== '' ||
        usdRequestsParams.end !== '' ||
        usdRequestsParams.status !== '' ||
        usdRequestsParams.startDate !== '' ||
        usdRequestsParams.endDate !== '') &&
      !canResetSearch
    );
  }, [usdRequestsParams, canResetSearch]);

  const fetchUSDRequests = async (paramObj?: any, fetchingMore = false, reset = false) => {
    try {
      fetchingMore ? setIsLoadingMore(true) : setIsLoading(true);

      // If reset is true, reset the params to default values
      if (reset) {
        const defaultParams = {
          limit: 50,
          start: '',
          end: '',
          search: '',
          status: '',
          userId: '',
          startDate: '',
          endDate: '',
          cursor: '',
        };
        setUsdRequestsParams(defaultParams);

        setUSDTimeParams({
          limit: 1000,
          search: '',
          status: '',
          start_date: {
            name: 'All Time',
            date: '',
          },
          end_date: {
            name: 'All Time',
            date: '',
          },
          isTimeFilterApplied: false,
        });

        paramObj = { ...defaultParams, page: 1 };
      }

      if (!paramObj) {
        paramObj = { ...usdRequestsParams };
      }

      const response = await httpClient.get(
        (requestType === 'held' ? 'admin/withheld-transactions/all' : `admin/flex-dollar-kyc/all`) +
          queryParamBuilder({ ...paramObj, page: paramObj?.page ?? usdRequestsData.nextPage, test: isTest }),
      );
      if (response.data?.status) {
        switch (requestType) {
          case 'all':
            {
              const { documents, currentPage, totalPage, page, total } = response.data.data.data;
              setUsdRequestsData({
                documents: fetchingMore ? [...usdRequestsData.documents, ...documents] : documents,
                currentPage,
                nextPage: page + 1,
                lastPage: totalPage,
                cursor: '',
                total,
              });

              dispatch(
                setUSDRequest({
                  documents: fetchingMore ? [...usdRequestsData.documents, ...documents] : documents,
                  currentPage,
                  nextPage: page + 1,
                  lastPage: totalPage,
                  total,
                }),
              );
            }
            break;
          case 'held':
            {
              const { requests, cursor } = response.data.data;
              setUsdRequestsData({
                documents: fetchingMore ? [...usdRequestsData.documents, ...requests] : requests,
                cursor,
                currentPage: 1,
                nextPage: 1,
                lastPage: 0,
                total: 0,
              });
              dispatch(
                setUSDHeldRequest({
                  requests: fetchingMore ? [...usdRequestsData.documents, ...requests] : requests,
                  cursor,
                }),
              );
            }
            break;
        }
      }
    } catch (error) {
      console.log(error);
      return error;
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      fetchUSDRequestsMetrics();
    }
  };

  const fetchUSDRequestsMetrics = async () => {
    try {
      setIsLoadingMetrics(true);

      const response = await httpClient.get(
        (requestType === 'held' ? `admin/withheld-transactions/metrics` : `admin/flex-dollar-kyc/metrics`) +
          queryParamBuilder({ ...usdRequestsParams }),
      );
      if (response.data?.status) {
        if (requestType === 'held') {
          const { total, withheld, released, rejected } = response.data.data;

          dispatch(
            setUSDRequestMetrics({
              totalHeld: total,
              withheld: withheld,
              released: released,
              rejected: rejected,
            }),
          );
        } else {
          const { total, approved, processing, returned, under_review } = response.data.data.data;

          dispatch(
            setUSDRequestMetrics({
              total: total,
              approved: approved,
              processing: processing,
              returned: returned,
              under_review: under_review,
            }),
          );
        }
      }
    } catch (error) {
      console.log(error);
      return error;
    } finally {
      setIsLoadingMetrics(false);
    }
  };

  const releaseOrRejectUSDRequest = async (
    action: 'release-usd-request' | 'reject-usd-request',
    id: string,
    reason?: string,
    callback?: () => void,
  ) => {
    try {
      setIsLoading(true);
      const response = (await httpClient.post(
        `admin/withheld-transactions${action === 'release-usd-request' ? 'release' : 'reject'}/${id}`,
        {
          reason,
        },
      )) as any;
      if (response.data.status) {
        Alert.success(response.data.message);
        callback?.();
      } else {
        Alert.error(response.data.message);
      }
    } catch (error) {
      console.log(error);
      return error;
    } finally {
      setIsLoading(false);
    }
  };
  const {
    data: singleUSDRequest,
    isLoading: isLoadingSingleUSDRequests,
    // isLoading: isLoadingHouseMoneyDetails,
    isRefetching: isRefetchingSingUSDRequest,
    refetch: refetchSingUSDRequest,
  } = useQuery({
    queryKey: ['usd-requests' + requestId + userId],
    queryFn: () =>
      httpClient.get(`admin/flex-dollar-kyc/single/${requestId}/${userId}/?test=${isTest}`).then((res) => {
        return {
          ...res.data.data.data.document,
          kyc_type: res.data.data.data?.kyc_type || '',
          review_needed: res.data.data.data?.review_needed ?? false,
        };
      }),
    enabled: !!requestId && !!userId,
  });

  const handleChange = (e: { target: { name: string; value: string | string[] } }) => {
    const { name, value } = e.target;
    setReturnForm({ ...returnForm, [name]: value });
  };

  const approveReturnRequest = async (fn: () => void, type: string) => {
    // const actionType = type === 'return' ? type : 'approve';

    // if (restrictActionByRole('usdRequest', actionType)) return;

    setIsLoading(true);

    let data: any = {
      docId: Number(requestId),
      userId,
      status: 'approved',
      test: isTest,
    };

    if (type === 'return') {
      data = {
        ...data,
        status: 'returned',
        ...returnForm,
      };
    }

    try {
      const response = await httpClient.post(`admin/flex-dollar-kyc/status`, data);
      if (response.data.status) {
        fn?.();
        let message = 'Request has been sent to the provider successfully';
        if (type === 'return') {
          message = 'Request returned successfully';
        }
        navigate('/usd-request');

        Alert.success(response?.data.message || message);
        dispatch(setModalActions({}));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Only the fields the admin actually touched are sent; docId/userId are the
  // only required keys on the endpoint.
  const updateUSDDocumentFields = async (changes: Record<string, any>, onSuccess?: () => void) => {
    if (!Object.keys(changes).length) {
      Alert.info('No changes were made');
      return;
    }

    setIsUpdatingDocument(true);

    try {
      const response = await httpClient.post<any>(`admin/flex-dollar-kyc/update-document`, {
        docId: Number(requestId),
        userId,
        ...changes,
      });

      if (response.data.status) {
        await refetchSingUSDRequest();
        Alert.success(response?.data?.message || 'Request details updated successfully');
        onSuccess?.();
      } else {
        Alert.error(response?.data?.message || 'An error occurred while trying to update the request details');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsUpdatingDocument(false);
    }
  };

  const updateUSDDocumentFile = async (payload: UpdateUSDDocumentFilePayload, onSuccess?: () => void) => {
    setIsUpdatingDocumentFile(true);

    try {
      const form = new FormData();
      form.append('docId', String(Number(requestId)));
      form.append('userId', userId);
      form.append('fileField', payload.fileField);
      form.append('base64', payload.base64);
      form.append('fileName', payload.fileName);

      // Back image only applies to a driver's licence identity document.
      if (payload.backBase64 && payload.backFileName) {
        form.append('backBase64', payload.backBase64);
        form.append('backFileName', payload.backFileName);
      }

      const response = await httpClient.post<any>(`admin/flex-dollar-kyc/update-document-file`, form as any, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.status) {
        await refetchSingUSDRequest();
        Alert.success(response?.data?.message || 'Document updated successfully');
        onSuccess?.();
      } else {
        Alert.error(response?.data?.message || 'An error occurred while trying to update the document');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsUpdatingDocumentFile(false);
    }
  };

  const generateAccountStatementForUSD = async () => {
    setIsLoading(true);
    const data: any = {
      docId: Number(requestId),
      userId,
    };

    try {
      const response = await httpClient.post(`admin/flex-dollar-kyc/generate-statement`, data);

      if (response.data.status) {
        refetchSingUSDRequest();
        Alert.success(response?.data.message || 'Account statement generated successfully');
        refetchSingUSDRequest();
      } else {
        Alert.error(response?.data.message || 'An error occurred while trying to generate account statement');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    approveReturnRequest,
    isLoading,
    isLoadingMore,
    isLoadingSingleUSDRequests,
    setIsLoading,
    singleUSDRequest,
    handleChange,
    returnForm,
    fetchUSDRequests,
    usdRequestsData,
    usdRequestsParams,
    canResetSearch,
    canResetFilter,
    setUsdRequestsParams,
    isTest,
    isRefetchingSingUSDRequest,
    generateAccountStatementForUSD,
    fetchUSDRequestsMetrics,
    isLoadingMetrics,
    showTodaysRequest,
    viewTodaysRequest,
    setViewTodaysRequest,
    setUSDTimeParams,
    USDTimeParams,
    releaseOrRejectUSDRequest,
    updateUSDDocumentFields,
    updateUSDDocumentFile,
    isUpdatingDocument,
    isUpdatingDocumentFile,
    refetchSingUSDRequest,
  };
};

export default useUSDRequest;
